import { useEffect, useState, useRef, useLayoutEffect } from "react";
import styles from './CampoModeloMensagem.module.css';

// Hook para medir texto de forma precisa
function useTextMeasure() {
    const measureRef = useRef<HTMLSpanElement | null>(null);

    // Criar o elemento de medição no primeiro render
    useLayoutEffect(() => {
        if (!measureRef.current) {
            const span = document.createElement('span');
            span.style.position = 'absolute';
            span.style.visibility = 'hidden';
            span.style.whiteSpace = 'pre';
            span.style.left = '-9999px';
            span.style.top = '-9999px';
            document.body.appendChild(span);
            measureRef.current = span;
        }

        // Limpar na desmontagem do componente
        return () => {
            if (measureRef.current) {
                document.body.removeChild(measureRef.current);
                measureRef.current = null;
            }
        };
    }, []);

    // Função para medir a largura de um texto
    const measureWidth = (text: string, font?: string): number => {
        const el = measureRef.current;
        if (!el) return 0;

        if (font) {
            el.style.font = font;
        }

        el.textContent = text;
        return el.getBoundingClientRect().width;
    };

    return measureWidth;
}

interface CampoModeloMensagemProps {
    texto: string;
    initialParams?: Record<string, string>;
    onParamsChange?: (params: Record<string, string>) => void;
    paramErrors?: Record<string, string>;
    disabled?: boolean;
}

const CampoModeloMensagem: React.FC<CampoModeloMensagemProps> = ({
    texto,
    initialParams = {},
    onParamsChange,
    paramErrors,
    disabled = false,
}) => {
    const [params, setParams] = useState<Record<string, string>>(initialParams);
    const measureText = useTextMeasure();

    // Extrair parâmetros do texto
    const extrairParametros = (texto: string): string[] => {
        const regex = /{{([^{}]+)}}/g;
        const parametros: string[] = [];
        let match;

        while ((match = regex.exec(texto)) !== null) {
            if (!parametros.includes(match[1])) {
                parametros.push(match[1]);
            }
        }

        return parametros;
    };

    // Processar alterações de parâmetros
    const handleParamChange = (param: string, value: string) => {
        const novosParams = { ...params, [param]: value };
        setParams(novosParams);
        onParamsChange?.(novosParams);
    };

    useEffect(() => {
        // Inicializar params com valores vazios para parâmetros que ainda não existem
        const parametrosEncontrados = extrairParametros(texto);
        const novosParams = { ...params };
        let alterado = false;

        parametrosEncontrados.forEach(param => {
            if (novosParams[param] === undefined) {
                novosParams[param] = '';
                alterado = true;
            }
        });

        if (alterado) {
            setParams(novosParams);
            onParamsChange?.(novosParams);
        }
    }, [texto]);

    // Efeito para ajustar o tamanho dos inputs quando os parâmetros mudam
    useEffect(() => {
        // Para cada parâmetro, ajustar o tamanho do input correspondente
        Object.keys(params).forEach(paramName => {
            const input = inputRefs.current[paramName];
            if (input) {
                ajustarInputSize(input);
            }
        });
    }, [params]);

    const regex = /{{([^{}]+)}}/g;
    const linhas = texto.split('\n').map(line => line.split(regex));

    // UseRef para rastrear os inputs e ajustar tamanho
    const inputRefs = useRef<Record<string, HTMLInputElement>>({});

    // Função para ajustar automaticamente o tamanho do input usando o elemento fantasma
    const ajustarInputSize = (element: HTMLInputElement | null) => {
        if (!element) return;

        // Obter o estilo computado do input para usar a mesma fonte na medição
        const computedStyle = window.getComputedStyle(element);
        const font = computedStyle.font || `${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`;

        // Padding interno do input - pegamos dos estilos computados
        const paddingLeft = parseFloat(computedStyle.paddingLeft || '0');
        const paddingRight = parseFloat(computedStyle.paddingRight || '0');

        // Medir a largura exata do texto
        const textoParaMedir = element.value || element.placeholder || '';
        const textWidth = measureText(textoParaMedir, font);

        // Adicionar padding ao tamanho + um pouco de espaço para o cursor
        const totalWidth = textWidth + paddingLeft + paddingRight + 12; // 12px para o cursor

        // Limitar entre min e max
        const minWidth = 100;
        const maxWidth = 300;
        const finalWidth = Math.min(Math.max(totalWidth, minWidth), maxWidth);

        // Aplicar a largura calculada
        element.style.width = `${finalWidth}px`;
    };

    // Função para cálculo inicial do tamanho do input (antes de ser renderizado)
    const calcularTamanhoInput = (valor: string): React.CSSProperties => {
        // Este é um cálculo aproximado até que o DOM esteja disponível para medição real
        const minWidth = 100;
        const textWidth = (valor?.length || 0) * 8 + 20;
        return {
            width: `${Math.min(Math.max(textWidth, minWidth), 300)}px`,
        };
    };

    return (
        <div>
            {linhas.map((linha, linhaIndex) => (
                <div key={linhaIndex} className={styles.container}>
                    {linha.map((parte, index) => {
                        if (index % 2 === 0) {
                            return <span key={index}>{parte}</span>;
                        } else {
                            const paramName = parte;
                            const valor = params[paramName] || '';

                            return (
                                <div className={[styles.paramContainer, paramErrors?.[paramName] ? styles.error : ''].join(' ')} key={index}>
                                    <input
                                        ref={(el) => {
                                            if (el) {
                                                inputRefs.current[paramName] = el;
                                                ajustarInputSize(el);
                                            }
                                        }}
                                        disabled={disabled}
                                        style={calcularTamanhoInput(valor)}
                                        value={valor}
                                        onChange={(e) => {
                                            handleParamChange(paramName, e.target.value);
                                            // Re-ajustar o tamanho após a alteração
                                            setTimeout(() => ajustarInputSize(inputRefs.current[paramName]), 0);
                                        }}
                                        placeholder={paramName}
                                    />
                                    {paramErrors && paramErrors[paramName] && (
                                        <span className={styles.error}>
                                            {paramErrors[paramName]}
                                        </span>
                                    )}
                                </div>
                            );
                        }
                    })}
                </div>
            ))}
        </div>
    );
};

export default CampoModeloMensagem;